import { DisplayMoney } from '@tock/money';
import { Typography } from 'omakase';
import { core } from '@tock/coretypes';
import { getBusinessPossessiveName } from 'utils/businessUtils';
import _ from 'lodash';

interface Props {
  businessName?: string;
  communicationPolicy?: core.ICommunicationPolicy;
  hasExternalGiftCardPayment?: boolean;
  hasLoyaltyPointsPayment?: boolean;
  isFreeBooking: boolean;
  ticketPriceInformation?: core.ITicketPriceInformation;
}

const CustomCancellationPolicyCard: React.FunctionComponent<Props> = ({
  businessName,
  isFreeBooking,
  hasExternalGiftCardPayment,
  hasLoyaltyPointsPayment,
  communicationPolicy,
  ticketPriceInformation,
}) => {
  const getPolicyText = () => {
    if (!communicationPolicy) {
      const policy: string[] = [];
      policy.push('All sales are final and non-refundable.');
      const transferText = addTransferReservationLineIfApplicable();
      if (transferText) {
        policy.push(transferText);
      }
      return policy;
    }

    if (
      communicationPolicy.showPolicyForFreeReservations &&
      communicationPolicy.cancellationPolicyText
    ) {
      return [communicationPolicy.cancellationPolicyText, getNonRefundablePaymentMessage()];
    }

    // set for free reservations and in concierge is false
    if (isFreeBooking) {
      return [
        'Your reservation can be canceled at any time before the time of the ' +
          'reservation should your plans change. However, we request that you ' +
          'do so at least 24 hours in advance so ' +
          (businessName || 'that we') +
          ' can plan accordingly.',
      ];
    }

    const { cancellationPolicyText, canRescheduleThroughTock, canCancelThroughTock } =
      communicationPolicy;

    if (canCancelThroughTock) {
      if (cancellationPolicyText) {
        return [
          cancellationPolicyText,
          addTransferReservationLineIfApplicable(),
          getNonRefundablePaymentMessage(),
        ];
      }
      return [
        'Your reservation can be canceled at any time before the time of the ' +
          'reservation should your plans change. However, we request that you ' +
          'do so at least 24 hours in advance so ' +
          (businessName || 'that we') +
          ' can plan accordingly.',
        getNonRefundablePaymentMessage(),
      ];
    }

    if (!canCancelThroughTock) {
      const policy: string[] = [];
      if (cancellationPolicyText) {
        return [cancellationPolicyText];
      }
      policy.push(
        canRescheduleThroughTock
          ? 'All sales are non-refundable.'
          : 'All sales are final and non-refundable.'
      );

      if (canRescheduleThroughTock) {
        policy.push(
          ' Your reservation can be rescheduled at any time before the time of the ' +
            'reservation should your plans change. However, we request that you ' +
            'do so at least 24 hours in advance so ' +
            (businessName || 'that we') +
            ' can plan accordingly.'
        );
      }

      const transferText = addTransferReservationLineIfApplicable();
      if (transferText) {
        policy.push(transferText);
      }
      return policy;
    }

    return [];
  };

  const addTransferReservationLineIfApplicable = () => {
    if (
      hasExternalGiftCardPayment ||
      hasLoyaltyPointsPayment ||
      !communicationPolicy?.canTransfer
    ) {
      return '';
    }
    return 'You can always transfer your reservation to another person.';
  };

  const getNonRefundablePaymentMessage = () => {
    if (hasExternalGiftCardPayment && hasLoyaltyPointsPayment) {
      return craftRefundablePaymentMessage('a gift card and loyalty points');
    } else if (hasExternalGiftCardPayment) {
      return craftRefundablePaymentMessage('a gift card');
    } else if (hasLoyaltyPointsPayment) {
      return craftRefundablePaymentMessage('loyalty points');
    } else {
      return '';
    }
  };

  const craftRefundablePaymentMessage = (payment: string) => {
    return `Because you paid with ${payment} tied to the business, you’ll have to contact them directly for any modifications that may result in a refund.`;
  };

  const getHoldLineIfApplicable = () => {
    if (
      !ticketPriceInformation ||
      ticketPriceInformation.priceType !== 'PAYMENT_CARD_HOLD' ||
      !ticketPriceInformation.amountCents // If price is either 0, null or undefined, nothing to show.
    ) {
      return null;
    }

    const distributionText =
      ticketPriceInformation.priceDistribution === 'PER_PARTY' ? '' : ' per person';

    return (
      <li key="hold-fee-line">
        <Typography variant="body02" data-testid="cancel-fee-policy">
          In the event of a no-show or late cancellation, you will be subject to a fee of{' '}
          <DisplayMoney amount={ticketPriceInformation.amountCents} /> {distributionText} charged to
          the payment method used to secure this reservation.
        </Typography>
      </li>
    );
  };

  const possesiveBusinessName = businessName ? getBusinessPossessiveName(businessName) : 'Business';
  return (
    <div className="ConsumerCheckout-section">
      <h2 className="ConsumerCheckout--title">
        <Typography
          variant="body02Strong"
          component="span"
          data-testid="confirm-cancellation-policy"
        >{`${possesiveBusinessName} cancellation policy`}</Typography>
      </h2>
      <ul className="CommunicationPolicy-list">
        {_.map(
          _.flatMap(getPolicyText().filter(Boolean), (str) => str.split(/\n+/)),
          (text: string, idx: number) => {
            return (
              <li key={idx}>
                <Typography variant="body02">{text}</Typography>
              </li>
            );
          }
        )}
        {getHoldLineIfApplicable()}
      </ul>
    </div>
  );
};

export default CustomCancellationPolicyCard;
